import React, { useCallback, useEffect, useRef, useState } from "react";
import { Card } from "primereact/card";
import { Toast } from "primereact/toast";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";

import { apiFetch } from "../../Helper/Api";
import { t } from "../../Helper/i18n";
import { byDay, monthCells, moveEntry, shiftMonth, todayCairo } from "../../Helper/calendarGrid";
import Header from "../../components/Header/Header";
import { Modal, Confirm } from "../../components/UI/Overlays";
import { EmptyState } from "../../components/UI/Feedback";
import { Segmented } from "../../components/UI/Controls";
import "./GymCalendar.scss";

const properties = require("../../properties.json");

const EMPTY_CAL = { name: "", planId: null };
const WEEK_START = 6; // Saturday first

/** The monthly workout calendars: pick a calendar, put a video, a recipe, a program or a note on any day, and drag an
 *  entry to another day to move it. A calendar can belong to one plan so only its members see it. */
export default function GymCalendar() {
  const toast = useRef(null);
  const [calendars, setCalendars] = useState(null);
  const [calendarId, setCalendarId] = useState(null);
  const [plans, setPlans] = useState([]);
  const [month, setMonth] = useState(todayCairo().slice(0, 7));
  const [entries, setEntries] = useState([]);
  const [calForm, setCalForm] = useState(null);
  const [dayForm, setDayForm] = useState(null);
  const [doomedCal, setDoomedCal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [libraryItems, setLibraryItems] = useState([]);
  const [programs, setPrograms] = useState([]);
  const dragging = useRef(null);

  const say = (severity, detail) => toast.current && toast.current.show({ severity, summary: severity === "error" ? t("common.alert") : t("common.done"), detail });
  const fail = (e) => say("error", (e && e.message) || t("common.failed"));

  const loadCalendars = useCallback(
    (select) =>
      apiFetch("/calendar/calendars")
        .then((list) => {
          setCalendars(list);
          setCalendarId((cur) => select || (list.some((c) => c.id === cur) ? cur : list.length ? list[0].id : null));
        })
        .catch(() => setCalendars([])),
    []
  );

  const loadMonth = useCallback(() => {
    if (!calendarId) {
      setEntries([]);
      return;
    }
    apiFetch(`/calendar/calendars/${calendarId}/entries`, { query: { month } })
      .then((m) => setEntries(m.entries))
      .catch(fail);
  }, [calendarId, month]);

  useEffect(() => {
    document.title = properties.site_name + " | " + t("gym.calendar");
    loadCalendars();
    apiFetch("/plans").then(setPlans).catch(() => {});
    apiFetch("/library", { query: { size: 50, sort: "title" } }).then((p) => setLibraryItems(p.items)).catch(() => {});
    apiFetch("/courses").then((c) => setPrograms(Array.isArray(c) ? c : [])).catch(() => {});
  }, [loadCalendars]);

  useEffect(loadMonth, [loadMonth]);

  const current = (calendars || []).find((c) => c.id === calendarId);

  // ---- calendars ----
  const saveCalendar = () => {
    if (!calForm.name.trim()) {
      say("error", t("gym.calendar.nameRequired"));
      return;
    }
    setSaving(true);
    const body = { name: calForm.name.trim(), planId: calForm.planId || 0, enabled: calForm.enabled !== false };
    apiFetch(calForm.id ? `/calendar/calendars/${calForm.id}` : "/calendar/calendars", { method: calForm.id ? "PUT" : "POST", body })
      .then((c) => {
        setCalForm(null);
        say("success", t("gym.calendar.saved"));
        return loadCalendars(c.id);
      })
      .catch(fail)
      .finally(() => setSaving(false));
  };

  const removeCalendar = () =>
    apiFetch(`/calendar/calendars/${calendarId}`, { method: "DELETE" })
      .then(() => {
        setDoomedCal(false);
        setCalendarId(null);
        return loadCalendars();
      })
      .catch((e) => {
        setDoomedCal(false);
        fail(e);
      });

  // ---- entries ----
  const openDay = (day) => setDayForm({ day, kind: "ITEM", itemId: null, courseId: null, title: "", note: "" });

  const saveEntry = () => {
    const f = dayForm;
    const body = { day: f.day, note: f.note || undefined };
    if (f.kind === "ITEM") body.itemId = f.itemId;
    else if (f.kind === "PROGRAM") body.courseId = f.courseId;
    else body.title = f.title;
    if ((f.kind === "ITEM" && !f.itemId) || (f.kind === "PROGRAM" && !f.courseId) || (f.kind === "NOTE" && !f.title.trim())) {
      say("error", t("gym.calendar.pickSomething"));
      return;
    }
    setSaving(true);
    apiFetch(`/calendar/calendars/${calendarId}/entries`, { method: "POST", body })
      .then(() => {
        setDayForm(null);
        loadMonth();
        return loadCalendars(calendarId);
      })
      .catch(fail)
      .finally(() => setSaving(false));
  };

  const removeEntry = (e) =>
    apiFetch(`/calendar/entries/${e.id}`, { method: "DELETE" })
      .then(() => {
        loadMonth();
        return loadCalendars(calendarId);
      })
      .catch(fail);

  const dropOn = (day) => {
    const id = dragging.current;
    dragging.current = null;
    if (!id) return;
    const before = entries;
    setEntries(moveEntry(entries, id, day));
    apiFetch(`/calendar/entries/${id}`, { method: "PUT", body: { day } }).catch((e) => {
      setEntries(before);
      fail(e);
    });
  };

  const cells = monthCells(month, WEEK_START);
  const days = byDay(entries);
  const names = ["أحد", "اتنين", "تلات", "أربع", "خميس", "جمعة", "سبت"];
  const header = Array.from({ length: 7 }, (_, i) => names[(WEEK_START + i) % 7]);
  const label = new Date(`${month}-15T12:00:00Z`).toLocaleDateString("ar-EG-u-nu-latn", { month: "long", year: "numeric", timeZone: "UTC" });
  const today = todayCairo();
  const planOptions = [{ label: t("gym.calendar.allMembers"), value: null }, ...plans.map((p) => ({ label: p.name, value: p.id }))];

  return (
    <>
      <Header pageName={t("gym.calendar")} action={{ label: t("gym.calendar.new"), icon: "pi pi-plus", onClick: () => setCalForm({ ...EMPTY_CAL }) }} />
      <Toast ref={toast} />
      <Confirm visible={doomedCal} onHide={() => setDoomedCal(false)} name={current ? current.name : ""} note={t("gym.calendar.deleteNote")} onConfirm={removeCalendar} />

      <Modal
        visible={!!calForm}
        onHide={() => setCalForm(null)}
        title={calForm && calForm.id ? t("gym.calendar.edit") : t("gym.calendar.new")}
        footer={<button type="button" className="ui-btn" disabled={saving} onClick={saveCalendar}><i className="pi pi-check"></i> {t("common.save")}</button>}
      >
        {calForm ? (
          <div className="ui-modal-form">
            <label className="ui-filter">
              <span>{t("gym.calendar.name")}</span>
              <InputText value={calForm.name} maxLength={120} onChange={(e) => setCalForm({ ...calForm, name: e.target.value })} />
            </label>
            <label className="ui-filter">
              <span>{t("gym.calendar.forPlan")}</span>
              <Dropdown options={planOptions} value={calForm.planId} onChange={(e) => setCalForm({ ...calForm, planId: e.value })} />
            </label>
            {calForm.id ? (
              <label className="ui-filter is-inline">
                <input type="checkbox" checked={calForm.enabled !== false} onChange={(e) => setCalForm({ ...calForm, enabled: e.target.checked })} />
                <span>{t("gym.calendar.enabled")}</span>
              </label>
            ) : null}
          </div>
        ) : null}
      </Modal>

      <Modal
        visible={!!dayForm}
        onHide={() => setDayForm(null)}
        title={dayForm ? t("gym.calendar.addTo", { day: dayForm.day }) : ""}
        footer={<button type="button" className="ui-btn" disabled={saving} onClick={saveEntry}><i className="pi pi-check"></i> {t("gym.calendar.add")}</button>}
      >
        {dayForm ? (
          <div className="ui-modal-form">
            <Segmented
              value={dayForm.kind}
              onChange={(kind) => setDayForm({ ...dayForm, kind })}
              items={[
                { key: "ITEM", label: t("gym.calendar.kind.item") },
                { key: "PROGRAM", label: t("gym.calendar.kind.program") },
                { key: "NOTE", label: t("gym.calendar.kind.note") },
              ]}
            />
            {dayForm.kind === "ITEM" ? (
              <label className="ui-filter">
                <span>{t("gym.calendar.item")}</span>
                <Dropdown filter options={libraryItems.map((i) => ({ label: i.title, value: i.itemId }))} value={dayForm.itemId} onChange={(e) => setDayForm({ ...dayForm, itemId: e.value })} placeholder={t("gym.calendar.pick")} />
              </label>
            ) : null}
            {dayForm.kind === "PROGRAM" ? (
              <label className="ui-filter">
                <span>{t("gym.calendar.program")}</span>
                <Dropdown filter options={programs.map((c) => ({ label: c.title, value: c.id }))} value={dayForm.courseId} onChange={(e) => setDayForm({ ...dayForm, courseId: e.value })} placeholder={t("gym.calendar.pick")} />
              </label>
            ) : null}
            {dayForm.kind === "NOTE" ? (
              <label className="ui-filter">
                <span>{t("gym.calendar.noteTitle")}</span>
                <InputText value={dayForm.title} maxLength={160} placeholder={t("gym.calendar.restDay")} onChange={(e) => setDayForm({ ...dayForm, title: e.target.value })} />
              </label>
            ) : null}
            <label className="ui-filter">
              <span>{t("gym.calendar.note")}</span>
              <InputTextarea rows={2} maxLength={500} value={dayForm.note} onChange={(e) => setDayForm({ ...dayForm, note: e.target.value })} />
            </label>
          </div>
        ) : null}
      </Modal>

      <div className="content">
        <div className="container">
          <div className="card">
            <Card>
              {calendars === null ? null : calendars.length === 0 ? (
                <EmptyState icon="pi pi-calendar" title={t("gym.calendar.emptyTitle")} hint={t("gym.calendar.emptyText")} />
              ) : (
                <>
                  <div className="gcal-bar">
                    <Dropdown options={calendars.map((c) => ({ label: `${c.name}${c.enabled ? "" : " (" + t("gym.plans.off") + ")"}`, value: c.id }))} value={calendarId} onChange={(e) => setCalendarId(e.value)} />
                    {current ? (
                      <>
                        <span className="gcal-plan">{current.planId ? (plans.find((p) => p.id === current.planId) || {}).name : t("gym.calendar.allMembers")}</span>
                        <Button icon="pi pi-pencil" className="p-button-rounded p-button-secondary" onClick={() => setCalForm({ ...current })} />
                        <Button icon="pi pi-trash" className="p-button-rounded p-button-danger" onClick={() => setDoomedCal(true)} />
                      </>
                    ) : null}
                  </div>

                  <div className="gcal-head">
                    <Button icon="pi pi-chevron-right" className="p-button-rounded p-button-text" onClick={() => setMonth(shiftMonth(month, -1))} />
                    <h3>{label}</h3>
                    <Button icon="pi pi-chevron-left" className="p-button-rounded p-button-text" onClick={() => setMonth(shiftMonth(month, 1))} />
                    <span className="gcal-hint">{t("gym.calendar.dragHint")}</span>
                  </div>

                  <div className="gcal-grid">
                    {header.map((n) => <div className="gcal-dow" key={n}>{n}</div>)}
                    {cells.map((day, i) =>
                      day ? (
                        <div
                          key={day}
                          className={`gcal-day ${day === today ? "today" : ""}`}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={() => dropOn(day)}
                        >
                          <div className="gcal-day-head">
                            <b>{Number(day.slice(8))}</b>
                            <button type="button" className="gcal-add" aria-label={t("gym.calendar.add")} onClick={() => openDay(day)}><i className="pi pi-plus" /></button>
                          </div>
                          {(days[day] || []).map((e) => (
                            <div key={e.id} className="gcal-entry" draggable onDragStart={() => (dragging.current = e.id)} title={e.note || e.title}>
                              <span>{e.title}</span>
                              <button type="button" aria-label={t("gym.calendar.remove")} onClick={() => removeEntry(e)}><i className="pi pi-times" /></button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div key={`b${i}`} className="gcal-blank" />
                      )
                    )}
                  </div>
                </>
              )}
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}
